import React, { useState, useEffect, useRef, useCallback } from 'react';
import { motion } from 'framer-motion';
import {
    DollarSign, Package, ShoppingCart, TrendingUp,
    Eye, Plus, ArrowRight, Clock, CheckCircle,
    Star, Wallet, CreditCard, BarChart3,
    Truck, XCircle, Store,
    Bell, Settings, HelpCircle, ExternalLink, Sparkles, Inbox
} from 'lucide-react';
import { Link } from 'react-router-dom';
import DashboardLayout from '../components/dashboard/DashboardLayout';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { useAuth, API } from '../App';
import axios from 'axios';
import ProductQuickAdd from '../components/ProductQuickAdd';

const VendorDashboard = () => {
    const { user, token } = useAuth();
    const [timeRange, setTimeRange] = useState('week');
    const [loading, setLoading] = useState(true);
    const [showQuickAdd, setShowQuickAdd] = useState(false);
    const [stats, setStats] = useState({
        totalRevenue: 0,
        totalOrders: 0,
        totalProducts: 0,
        pendingBalance: 0,
    });
    const [orderBreakdown, setOrderBreakdown] = useState([]);
    const [recentOrders, setRecentOrders] = useState([]);
    const [topProducts, setTopProducts] = useState([]);
    const [announcements, setAnnouncements] = useState([]);

    const isInitialLoad = useRef(true);

    const fetchDashboardData = useCallback(async (silent = false) => {
        if (!silent) setLoading(true);
        try {
            // Fetch inventory
            const inventoryRes = await axios.get(`${API}/api/vendor/inventory`, {
                headers: { Authorization: `Bearer ${token}` }
            });

            // Fetch assigned orders
            const ordersRes = await axios.get(`${API}/api/vendor/assigned-orders`, {
                headers: { Authorization: `Bearer ${token}` }
            });

            const inventory = inventoryRes.data || [];
            const orders = ordersRes.data || [];

            // Calculate REAL stats only
            const completedOrders = orders.filter(o => o.status === 'completed');
            const totalRevenue = completedOrders.reduce((sum, o) => sum + (o.total_amount || 0), 0);
            const pendingBalance = orders
                .filter(o => o.status !== 'completed' && o.status !== 'cancelled')
                .reduce((sum, o) => sum + (o.total_amount || 0), 0);

            const orderCounts = {
                assigned: orders.filter(o => o.status === 'assigned').length,
                processing: orders.filter(o => o.status === 'processing').length,
                shipped: orders.filter(o => o.status === 'shipped').length,
                completed: orders.filter(o => o.status === 'completed').length,
                cancelled: orders.filter(o => o.status === 'cancelled').length
            };

            setStats({
                totalRevenue,
                totalOrders: orders.length,
                totalProducts: inventory.length,
                pendingBalance,
            });

            setOrderBreakdown([
                { status: 'Assigned', count: orderCounts.assigned, color: 'bg-yellow-500', icon: Clock },
                { status: 'Processing', count: orderCounts.processing, color: 'bg-blue-500', icon: Package },
                { status: 'Shipped', count: orderCounts.shipped, color: 'bg-purple-500', icon: Truck },
                { status: 'Completed', count: orderCounts.completed, color: 'bg-green-500', icon: CheckCircle },
                { status: 'Cancelled', count: orderCounts.cancelled, color: 'bg-red-500', icon: XCircle },
            ]);

            setRecentOrders(orders.slice(0, 4).map(o => ({
                id: o.id,
                customer: o.customer_name || 'Customer',
                product: o.items?.[0]?.name || 'Order Items',
                amount: o.total_amount || 0,
                status: o.status?.charAt(0).toUpperCase() + o.status?.slice(1),
                date: o.created_at ? new Date(o.created_at).toLocaleDateString() : 'N/A',
                avatar: (o.customer_name || 'C')[0].toUpperCase()
            })));

            setTopProducts(inventory.slice(0, 4).map(i => ({
                name: i.product_name || 'Product',
                stock: i.quantity || 0,
                price: i.vendor_price || 0,
                image: '☀️'
            })));

        } catch (error) {
            console.error('Failed to fetch dashboard data:', error);
            // On error, keep zeros - don't show fake data
            setStats({
                totalRevenue: 0,
                totalOrders: 0,
                totalProducts: 0,
                pendingBalance: 0,
            });
            setOrderBreakdown([
                { status: 'Assigned', count: 0, color: 'bg-yellow-500', icon: Clock },
                { status: 'Processing', count: 0, color: 'bg-blue-500', icon: Package },
                { status: 'Shipped', count: 0, color: 'bg-purple-500', icon: Truck },
                { status: 'Completed', count: 0, color: 'bg-green-500', icon: CheckCircle },
                { status: 'Cancelled', count: 0, color: 'bg-red-500', icon: XCircle },
            ]);
            setRecentOrders([]);
            setTopProducts([]);
        }

        // Fetch announcements
        try {
            const announcementsRes = await axios.get(`${API}/api/vendor/announcements`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setAnnouncements(announcementsRes.data.slice(0, 3)); // Show max 3 on dashboard
        } catch (e) {
            setAnnouncements([{
                title: 'Welcome to SolarSavers!',
                message: 'Start by adding products to your inventory',
                type: 'info',
                created_at: new Date().toISOString()
            }]);
        }

        if (!silent) setLoading(false);
    }, [token]);

    useEffect(() => {
        if (!token) return;
        fetchDashboardData();
        isInitialLoad.current = false;
        const interval = setInterval(() => fetchDashboardData(true), 30000);
        return () => clearInterval(interval);
    }, [token, fetchDashboardData]);

    const getStatusColor = (status) => {
        const colors = {
            'Assigned': 'bg-yellow-100 text-yellow-700',
            'Processing': 'bg-blue-100 text-blue-700',
            'Shipped': 'bg-purple-100 text-purple-700',
            'Completed': 'bg-green-100 text-green-700',
            'Cancelled': 'bg-red-100 text-red-700',
        };
        return colors[status] || 'bg-gray-100 text-gray-700';
    };

    // Empty state component
    const EmptyState = ({ icon: Icon, title, description, action }) => (
        <div className="flex flex-col items-center justify-center py-8 text-center">
            <div className="w-12 h-12 bg-muted rounded-full flex items-center justify-center mb-3">
                <Icon className="w-6 h-6 text-muted-foreground" />
            </div>
            <p className="font-medium text-sm">{title}</p>
            <p className="text-xs text-muted-foreground mt-1">{description}</p>
            {action}
        </div>
    );

    // Coming Soon component for features not yet implemented
    const ComingSoon = ({ title }) => (
        <div className="flex items-center justify-center py-6 text-center">
            <div className="flex items-center gap-2 text-muted-foreground">
                <Sparkles className="w-4 h-4" />
                <span className="text-sm">{title} - Coming Soon</span>
            </div>
        </div>
    );

    return (
        <DashboardLayout userRole="vendor">
            <div className="space-y-6">
                {/* Header with Store Info */}
                <div className="bg-gradient-to-r from-primary via-primary/90 to-primary/80 rounded-2xl p-6 text-white relative overflow-hidden">
                    <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full -translate-y-1/2 translate-x-1/2" />
                    <div className="absolute bottom-0 left-1/2 w-32 h-32 bg-white/5 rounded-full translate-y-1/2" />

                    <div className="relative z-10 flex flex-col md:flex-row md:items-center justify-between gap-4">
                        <div className="flex items-center gap-4">
                            <div className="w-16 h-16 bg-white/20 backdrop-blur rounded-xl flex items-center justify-center">
                                <Store className="w-8 h-8" />
                            </div>
                            <div>
                                <h1 className="text-2xl font-bold">Welcome back, {user?.name || 'Vendor'}!</h1>
                                <p className="text-white/80 flex items-center gap-2 mt-1">
                                    <Star className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                                    Vendor Dashboard • Manage your store
                                </p>
                            </div>
                        </div>
                        <div className="flex items-center gap-3">
                            <Button variant="ghost" className="text-white hover:bg-white/20">
                                <ExternalLink className="w-4 h-4 mr-2" />Visit Store
                            </Button>
                            <Button
                                onClick={() => setShowQuickAdd(true)}
                                className="bg-white text-primary hover:bg-white/90"
                            >
                                <Plus className="w-4 h-4 mr-2" />Quick Add Product
                            </Button>
                        </div>
                    </div>
                </div>

                {/* Time Range Selector */}
                <div className="flex items-center justify-between">
                    <h2 className="text-lg font-semibold">Overview</h2>
                    <div className="flex bg-muted rounded-lg p-1">
                        {['today', 'week', 'month', 'year'].map((range) => (
                            <button
                                key={range}
                                onClick={() => setTimeRange(range)}
                                className={`px-4 py-1.5 text-sm font-medium rounded-md transition-all capitalize ${timeRange === range
                                    ? 'bg-white text-primary shadow-sm'
                                    : 'text-muted-foreground hover:text-foreground'
                                    }`}
                            >
                                {range}
                            </button>
                        ))}
                    </div>
                </div>

                {/* Stats Grid */}
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                    {/* Revenue Card */}
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="bg-card rounded-xl border p-5 hover:shadow-md transition-shadow"
                    >
                        <div className="flex items-center justify-between mb-3">
                            <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                                <DollarSign className="w-5 h-5 text-green-600" />
                            </div>
                        </div>
                        <p className="text-2xl font-bold">₹{stats.totalRevenue.toLocaleString()}</p>
                        <p className="text-sm text-muted-foreground mt-1">Total Revenue</p>
                    </motion.div>

                    {/* Orders Card */}
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.1 }}
                        className="bg-card rounded-xl border p-5 hover:shadow-md transition-shadow"
                    >
                        <div className="flex items-center justify-between mb-3">
                            <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                                <ShoppingCart className="w-5 h-5 text-blue-600" />
                            </div>
                        </div>
                        <p className="text-2xl font-bold">{stats.totalOrders}</p>
                        <p className="text-sm text-muted-foreground mt-1">Total Orders</p>
                    </motion.div>

                    {/* Products Card */}
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.2 }}
                        className="bg-card rounded-xl border p-5 hover:shadow-md transition-shadow"
                    >
                        <div className="flex items-center justify-between mb-3">
                            <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                                <Package className="w-5 h-5 text-purple-600" />
                            </div>
                            {stats.totalProducts > 0 && (
                                <Badge className="bg-green-100 text-green-700 text-xs">{stats.totalProducts} active</Badge>
                            )}
                        </div>
                        <p className="text-2xl font-bold">{stats.totalProducts}</p>
                        <p className="text-sm text-muted-foreground mt-1">Products in Inventory</p>
                    </motion.div>

                    {/* Store Views Card - Coming Soon */}
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.3 }}
                        className="bg-card rounded-xl border p-5 hover:shadow-md transition-shadow"
                    >
                        <div className="flex items-center justify-between mb-3">
                            <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
                                <Eye className="w-5 h-5 text-orange-600" />
                            </div>
                            <Badge variant="outline" className="text-xs">Soon</Badge>
                        </div>
                        <p className="text-2xl font-bold text-muted-foreground">--</p>
                        <p className="text-sm text-muted-foreground mt-1">Store Views</p>
                    </motion.div>
                </div>

                {/* Earnings & Orders Grid */}
                <div className="grid lg:grid-cols-3 gap-6">
                    {/* Earnings Card */}
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="bg-card rounded-xl border p-6"
                    >
                        <div className="flex items-center justify-between mb-6">
                            <h3 className="font-semibold flex items-center gap-2">
                                <Wallet className="w-5 h-5 text-primary" />
                                Earnings
                            </h3>
                            <Button variant="outline" size="sm" disabled>Withdraw</Button>
                        </div>

                        <div className="space-y-4">
                            <div className="flex items-center justify-between p-4 bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl border border-green-200">
                                <div>
                                    <p className="text-sm text-muted-foreground">Pending Balance</p>
                                    <p className="text-2xl font-bold text-green-600">₹{stats.pendingBalance.toLocaleString()}</p>
                                </div>
                                <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                                    <CreditCard className="w-6 h-6 text-green-600" />
                                </div>
                            </div>

                            <div className="grid grid-cols-2 gap-3">
                                <div className="p-3 bg-muted/50 rounded-lg">
                                    <p className="text-xs text-muted-foreground">Processing</p>
                                    <p className="font-semibold">₹{stats.pendingBalance.toLocaleString()}</p>
                                </div>
                                <div className="p-3 bg-muted/50 rounded-lg">
                                    <p className="text-xs text-muted-foreground">Completed</p>
                                    <p className="font-semibold">₹{stats.totalRevenue.toLocaleString()}</p>
                                </div>
                            </div>
                        </div>
                    </motion.div>

                    {/* Order Status Breakdown */}
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.1 }}
                        className="bg-card rounded-xl border p-6"
                    >
                        <div className="flex items-center justify-between mb-6">
                            <h3 className="font-semibold flex items-center gap-2">
                                <BarChart3 className="w-5 h-5 text-primary" />
                                Order Status
                            </h3>
                            <Link to="/vendor/orders">
                                <Button variant="ghost" size="sm">View All</Button>
                            </Link>
                        </div>

                        {stats.totalOrders === 0 ? (
                            <EmptyState
                                icon={Inbox}
                                title="No orders yet"
                                description="Orders assigned to you will appear here"
                            />
                        ) : (
                            <div className="space-y-3">
                                {orderBreakdown.map((item) => (
                                    <div key={item.status} className="flex items-center gap-3">
                                        <div className={`w-8 h-8 ${item.color} rounded-lg flex items-center justify-center text-white`}>
                                            <item.icon className="w-4 h-4" />
                                        </div>
                                        <div className="flex-1">
                                            <div className="flex items-center justify-between mb-1">
                                                <span className="text-sm font-medium">{item.status}</span>
                                                <span className="text-sm font-bold">{item.count}</span>
                                            </div>
                                            <div className="h-1.5 bg-muted rounded-full overflow-hidden">
                                                <div
                                                    className={`h-full ${item.color} rounded-full transition-all`}
                                                    style={{ width: stats.totalOrders > 0 ? `${(item.count / stats.totalOrders) * 100}%` : '0%' }}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </motion.div>

                    {/* Sales Chart - Coming Soon */}
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.2 }}
                        className="bg-card rounded-xl border p-6"
                    >
                        <div className="flex items-center justify-between mb-6">
                            <h3 className="font-semibold flex items-center gap-2">
                                <TrendingUp className="w-5 h-5 text-primary" />
                                Sales Analytics
                            </h3>
                            <Badge variant="outline">Coming Soon</Badge>
                        </div>

                        <div className="flex flex-col items-center justify-center h-32 text-center">
                            <div className="w-12 h-12 bg-muted rounded-full flex items-center justify-center mb-3">
                                <BarChart3 className="w-6 h-6 text-muted-foreground" />
                            </div>
                            <p className="text-sm text-muted-foreground">
                                Detailed sales analytics will be available soon
                            </p>
                        </div>
                    </motion.div>
                </div>

                {/* Recent Orders & Top Products */}
                <div className="grid lg:grid-cols-2 gap-6">
                    {/* Recent Orders */}
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="bg-card rounded-xl border"
                    >
                        <div className="p-5 border-b flex items-center justify-between">
                            <h3 className="font-semibold">Recent Orders</h3>
                            <Link to="/vendor/orders">
                                <Button variant="ghost" size="sm">
                                    View All <ArrowRight className="w-4 h-4 ml-1" />
                                </Button>
                            </Link>
                        </div>
                        {recentOrders.length === 0 ? (
                            <div className="p-6">
                                <EmptyState
                                    icon={ShoppingCart}
                                    title="No orders yet"
                                    description="Your recent orders will appear here"
                                />
                            </div>
                        ) : (
                            <div className="divide-y">
                                {recentOrders.map((order) => (
                                    <div key={order.id} className="p-4 hover:bg-muted/30 transition-colors">
                                        <div className="flex items-center gap-4">
                                            <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center font-semibold text-primary">
                                                {order.avatar}
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <div className="flex items-center justify-between">
                                                    <p className="font-medium text-sm">{order.customer}</p>
                                                    <Badge className={getStatusColor(order.status)}>{order.status}</Badge>
                                                </div>
                                                <p className="text-xs text-muted-foreground truncate">{order.product}</p>
                                            </div>
                                            <div className="text-right">
                                                <p className="font-semibold">₹{order.amount.toLocaleString()}</p>
                                                <p className="text-xs text-muted-foreground">{order.date}</p>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </motion.div>

                    {/* Top Products / Inventory */}
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.1 }}
                        className="bg-card rounded-xl border"
                    >
                        <div className="p-5 border-b flex items-center justify-between">
                            <h3 className="font-semibold">Your Inventory</h3>
                            <Link to="/vendor/products">
                                <Button variant="ghost" size="sm">
                                    View All <ArrowRight className="w-4 h-4 ml-1" />
                                </Button>
                            </Link>
                        </div>
                        {topProducts.length === 0 ? (
                            <div className="p-6">
                                <EmptyState
                                    icon={Package}
                                    title="No products yet"
                                    description="Add products to your inventory to start selling"
                                    action={
                                        <Button
                                            size="sm"
                                            className="mt-3"
                                            onClick={() => setShowQuickAdd(true)}
                                        >
                                            <Plus className="w-4 h-4 mr-1" /> Add Product
                                        </Button>
                                    }
                                />
                            </div>
                        ) : (
                            <div className="divide-y">
                                {topProducts.map((product, index) => (
                                    <div key={index} className="p-4 hover:bg-muted/30 transition-colors">
                                        <div className="flex items-center gap-4">
                                            <div className="w-12 h-12 bg-muted rounded-lg flex items-center justify-center text-2xl">
                                                {product.image}
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <p className="font-medium text-sm truncate">{product.name}</p>
                                                <div className="flex items-center gap-3 mt-1">
                                                    <span className="text-xs text-muted-foreground">₹{product.price.toLocaleString()}</span>
                                                    <span className="text-xs px-2 py-0.5 bg-muted rounded-full">
                                                        {product.stock} in stock
                                                    </span>
                                                </div>
                                            </div>
                                            <div className="text-right">
                                                <Badge variant={product.stock > 10 ? 'default' : product.stock > 0 ? 'secondary' : 'destructive'}>
                                                    {product.stock > 10 ? 'In Stock' : product.stock > 0 ? 'Low Stock' : 'Out of Stock'}
                                                </Badge>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </motion.div>
                </div>

                {/* Announcements & Quick Actions */}
                <div className="grid lg:grid-cols-3 gap-6">
                    {/* Announcements */}
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="lg:col-span-2 bg-card rounded-xl border p-6"
                    >
                        <div className="flex items-center justify-between mb-4">
                            <h3 className="font-semibold flex items-center gap-2">
                                <Bell className="w-5 h-5 text-primary" />
                                Announcements
                            </h3>
                            <Link to="/vendor/support" className="text-sm text-primary hover:underline flex items-center gap-1">
                                View All <ArrowRight className="w-3 h-3" />
                            </Link>
                        </div>
                        {announcements.length === 0 ? (
                            <div className="text-center py-6 text-muted-foreground">
                                <Inbox className="w-8 h-8 mx-auto mb-2 opacity-50" />
                                <p className="text-sm">No announcements</p>
                            </div>
                        ) : (
                            <div className="space-y-3">
                                {announcements.map((item, index) => (
                                    <Link key={index} to="/vendor/support" className={`block p-4 rounded-lg border-l-4 hover:shadow-sm transition-shadow ${item.type === 'success' ? 'bg-green-50 border-green-500' : 'bg-blue-50 border-blue-500'
                                        }`}>
                                        <div className="flex items-start justify-between">
                                            <div>
                                                <p className="font-medium text-sm">{item.title}</p>
                                                <p className="text-sm text-muted-foreground mt-1 line-clamp-1">{item.message}</p>
                                            </div>
                                            <span className="text-xs text-muted-foreground">
                                                {item.created_at ? new Date(item.created_at).toLocaleDateString() : item.time}
                                            </span>
                                        </div>
                                    </Link>
                                ))}
                            </div>
                        )}
                    </motion.div>

                    {/* Quick Actions */}
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.1 }}
                        className="bg-card rounded-xl border p-6"
                    >
                        <h3 className="font-semibold mb-4">Quick Actions</h3>
                        <div className="space-y-2">
                            <button
                                onClick={() => setShowQuickAdd(true)}
                                className="w-full flex items-center gap-3 p-3 rounded-lg hover:bg-muted transition-colors text-left"
                            >
                                <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                                    <Plus className="w-5 h-5 text-primary" />
                                </div>
                                <span className="font-medium text-sm">Add New Product</span>
                            </button>
                            <Link to="/vendor/orders" className="flex items-center gap-3 p-3 rounded-lg hover:bg-muted transition-colors">
                                <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                                    <ShoppingCart className="w-5 h-5 text-blue-600" />
                                </div>
                                <span className="font-medium text-sm">Manage Orders</span>
                            </Link>
                            <Link to="/vendor/profile" className="flex items-center gap-3 p-3 rounded-lg hover:bg-muted transition-colors">
                                <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                                    <Settings className="w-5 h-5 text-purple-600" />
                                </div>
                                <span className="font-medium text-sm">Store Settings</span>
                            </Link>
                            <Link to="/vendor/support" className="flex items-center gap-3 p-3 rounded-lg hover:bg-muted transition-colors">
                                <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
                                    <HelpCircle className="w-5 h-5 text-orange-600" />
                                </div>
                                <div className="flex-1">
                                    <span className="font-medium text-sm block">Support & Contact</span>
                                    <span className="text-xs text-muted-foreground">Get help or raise a ticket</span>
                                </div>
                            </Link>
                        </div>
                    </motion.div>
                </div>
            </div>

            {/* Quick Add Product Modal */}
            <ProductQuickAdd
                isOpen={showQuickAdd}
                onClose={() => setShowQuickAdd(false)}
                onProductAdded={() => {
                    setShowQuickAdd(false);
                    // Refresh dashboard data
                }}
                token={token}
            />
        </DashboardLayout>
    );
};

export default VendorDashboard;
